import api from '../lib/api';

export interface SavingsSnapshot {
  id: string;
  organizationId: string;
  periodStart: string;
  periodEnd: string;
  baselineHeadcount: number;
  actualHeadcount: number;
  headcountSaved: number;
  baselineOvertimeHours: number;
  actualOvertimeHours: number;
  overtimeHoursSaved: number;
  hourlyRate: number;
  overtimeMultiplier: number;
  laborCostSavings: number;
  overtimeCostSavings: number;
  totalSavings: number;
  forecastAccuracy: number | null;
  schedulingEfficiency: number | null;
  notes: string | null;
  createdAt: string;
  createdBy: string | null;
}

export interface CreateSavingsSnapshotDto {
  organizationId: string;
  periodStart: string;
  periodEnd: string;
  baselineHeadcount: number;
  actualHeadcount: number;
  baselineOvertimeHours: number;
  actualOvertimeHours: number;
  hourlyRate: number;
  overtimeMultiplier?: number;
  forecastAccuracy?: number;
  schedulingEfficiency?: number;
  notes?: string;
}

export interface SavingsSummary {
  totalSavings: number;
  totalLaborSavings: number;
  totalOvertimeSavings: number;
  totalHeadcountSaved: number;
  totalOvertimeHoursSaved: number;
  averageForecastAccuracy: number | null;
  averageSchedulingEfficiency: number | null;
  snapshotCount: number;
  periodStart: string | null;
  periodEnd: string | null;
}

export interface CalculateSavingsParams {
  organizationId: string;
  periodStart: string;
  periodEnd: string;
  hourlyRate: number;
  overtimeMultiplier?: number;
  baselineEfficiencyPercent?: number;
}

export interface CalculatedSavings {
  baselineHeadcount: number;
  actualHeadcount: number;
  baselineOvertimeHours: number;
  actualOvertimeHours: number;
  forecastAccuracy: number | null;
  hourlyRate: number;
  overtimeMultiplier: number;
}

class ReportsService {
  async getSavingsHistory(
    organizationId: string,
    options?: {
      startDate?: string;
      endDate?: string;
      limit?: number;
    }
  ): Promise<SavingsSnapshot[]> {
    const params = new URLSearchParams({ organizationId });
    if (options?.startDate) params.append('startDate', options.startDate);
    if (options?.endDate) params.append('endDate', options.endDate);
    if (options?.limit) params.append('limit', options.limit.toString());

    const response = await api.get(`/reports/savings/history?${params}`);
    return response.data;
  }

  async getSavingsSummary(
    organizationId: string,
    options?: {
      startDate?: string;
      endDate?: string;
    }
  ): Promise<SavingsSummary> {
    const params = new URLSearchParams({ organizationId });
    if (options?.startDate) params.append('startDate', options.startDate);
    if (options?.endDate) params.append('endDate', options.endDate);

    const response = await api.get(`/reports/savings/summary?${params}`);
    return response.data;
  }

  async createSnapshot(dto: CreateSavingsSnapshotDto): Promise<SavingsSnapshot> {
    const response = await api.post('/reports/savings/snapshot', dto);
    return response.data;
  }

  async deleteSnapshot(snapshotId: string): Promise<void> {
    await api.delete(`/reports/savings/snapshot/${snapshotId}`);
  }

  async calculateSavings(params: CalculateSavingsParams): Promise<CalculatedSavings> {
    const response = await api.post('/reports/savings/calculate', params);
    return response.data;
  }
}

export const reportsService = new ReportsService();
